import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

/**
 * Save a study field for the authenticated user
 * @param {Object} field - The field object to save
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export const saveField = async (field) => {
  try {
    // Get authenticated user
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      logger?.error('FIELD_SERVICE', 'User not authenticated', { error: authError?.message });
      return { data: null, error: { message: 'Vous devez être connecté pour enregistrer une filière' } };
    }

    // Check if field is already saved
    const { data: existing, error: checkError } = await supabase?.from('saved_fields')?.select('id')?.eq('user_id', user?.id)?.eq('field_id', field?.id?.toString())?.maybeSingle();

    if (checkError) {
      logger?.error('FIELD_SERVICE', 'Error checking existing saved field', { error: checkError?.message });
      return { data: null, error: { message: 'Erreur lors de la vérification' } };
    }

    if (existing) {
      logger?.info('FIELD_SERVICE', 'Field already saved', { fieldId: field?.id });
      return { data: existing, error: { message: 'Cette filière est déjà enregistrée', alreadySaved: true } };
    }

    // Save the field
    const { data, error } = await supabase?.from('saved_fields')?.insert({
        user_id: user?.id,
        field_id: field?.id?.toString(),
        field_name: field?.name,
        field_data: {
          description: field?.description,
          compatibilityScore: field?.compatibilityScore,
          careerProspects: field?.careerProspects,
          marketDemand: field?.marketDemand,
          salaryRange: field?.salaryRange,
          employmentRate: field?.employmentRate
        }
      })?.select()?.single();

    if (error) {
      logger?.error('FIELD_SERVICE', 'Error saving field', { error: error?.message });
      return { data: null, error: { message: 'Erreur lors de l\'enregistrement de la filière' } };
    }

    logger?.info('FIELD_SERVICE', 'Field saved successfully', { fieldId: field?.id });
    return { data, error: null };
  } catch (error) {
    logger?.error('FIELD_SERVICE', 'Unexpected error in saveField', { error: error?.message });
    return { data: null, error: { message: 'Une erreur inattendue s\'est produite' } };
  }
};

/**
 * Share a study field with the user's assigned counselor
 * @param {Object} field - The field object to share
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export const shareFieldWithCounselor = async (field) => {
  try {
    // Get authenticated user
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      logger?.error('FIELD_SERVICE', 'User not authenticated', { error: authError?.message });
      return { data: null, error: { message: 'Vous devez être connecté pour partager avec un conseiller' } };
    }

    // Find the user's assigned counselor
    const { data: counselorRelation, error: counselorError } = await supabase?.from('counselor_students')?.select('counselor_id')?.eq('student_id', user?.id)?.maybeSingle();

    if (counselorError) {
      logger?.error('FIELD_SERVICE', 'Error finding counselor', { error: counselorError?.message });
      return { data: null, error: { message: 'Erreur lors de la recherche du conseiller' } };
    }

    if (!counselorRelation) {
      logger?.warn('FIELD_SERVICE', 'No counselor assigned', { userId: user?.id });
      return { data: null, error: { message: 'Aucun conseiller n\'est assigné à votre compte', noCounselor: true } };
    }

    // Create notification for counselor
    const { data, error } = await supabase?.from('counselor_notifications')?.insert({
        student_id: user?.id,
        counselor_id: counselorRelation?.counselor_id,
        notification_type: 'field_share',
        field_data: {
          id: field?.id,
          name: field?.name,
          description: field?.description,
          compatibilityScore: field?.compatibilityScore,
          careerProspects: field?.careerProspects,
          marketDemand: field?.marketDemand,
          salaryRange: field?.salaryRange
        },
        message: `L'étudiant souhaite discuter de la filière: ${field?.name}`,
        status: 'pending'
      })?.select()?.single();

    if (error) {
      logger?.error('FIELD_SERVICE', 'Error creating counselor notification', { error: error?.message });
      return { data: null, error: { message: 'Erreur lors du partage avec le conseiller' } };
    }

    logger?.info('FIELD_SERVICE', 'Field shared with counselor successfully', { 
      fieldId: field?.id,
      counselorId: counselorRelation?.counselor_id 
    });
    return { data, error: null };
  } catch (error) {
    logger?.error('FIELD_SERVICE', 'Unexpected error in shareFieldWithCounselor', { error: error?.message });
    return { data: null, error: { message: 'Une erreur inattendue s\'est produite' } };
  }
};

/**
 * Get all saved fields for the authenticated user
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export const getSavedFields = async () => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      return { data: null, error: { message: 'Vous devez être connecté' } };
    }

    const { data, error } = await supabase?.from('saved_fields')?.select('*')?.eq('user_id', user?.id)?.order('saved_at', { ascending: false });

    if (error) {
      logger?.error('FIELD_SERVICE', 'Error fetching saved fields', { error: error?.message });
      return { data: null, error: { message: 'Erreur lors de la récupération des filières enregistrées' } };
    }

    return { data: data || [], error: null };
  } catch (error) {
    logger?.error('FIELD_SERVICE', 'Unexpected error in getSavedFields', { error: error?.message });
    return { data: null, error: { message: 'Une erreur inattendue s\'est produite' } };
  }
};

/**
 * Remove a saved field
 * @param {string} fieldId - The field ID to remove
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export const removeSavedField = async (fieldId) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      return { data: null, error: { message: 'Vous devez être connecté' } };
    }

    const { error } = await supabase?.from('saved_fields')?.delete()?.eq('user_id', user?.id)?.eq('field_id', fieldId?.toString());

    if (error) {
      logger?.error('FIELD_SERVICE', 'Error removing saved field', { error: error?.message });
      return { data: null, error: { message: 'Erreur lors de la suppression' } };
    }

    logger?.info('FIELD_SERVICE', 'Field removed successfully', { fieldId });
    return { data: { success: true }, error: null };
  } catch (error) {
    logger?.error('FIELD_SERVICE', 'Unexpected error in removeSavedField', { error: error?.message });
    return { data: null, error: { message: 'Une erreur inattendue s\'est produite' } };
  }
};